
import React, { useState } from 'react';
import { Lightbulb, TrendingUp, Calendar, Tag, Users, Check, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import StationSelection from './segment-components/StationSelection';

const AICampaignRecommendations = ({ segmentId = 'urban', selectedStations = [] }) => {
  const [showStationSelection, setShowStationSelection] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Get segment-specific campaigns
  const getCampaignsForSegment = () => {
    const campaigns = {
      urban: [
        {
          id: 'urb-cam-1',
          name: 'Urban Premium Fuel Boost',
          description: 'Increase premium fuel adoption with a targeted loyalty point multiplier for premium grade purchases.',
          impact: 'high',
          kpis: ['Premium %', 'Margin', 'Loyalty'],
          projectedLift: '+8.5%',
          confidence: '92%',
          duration: '4 weeks',
          insights: 'Urban customers show high price elasticity for premium fuels when incentivized with loyalty benefits.'
        },
        {
          id: 'urb-cam-2',
          name: 'Café Amazon Bundle',
          description: 'Fuel purchase + café item discount bundle to drive cross-category purchases during morning commute.',
          impact: 'medium',
          kpis: ['NFR Sales', 'Basket Size', 'Traffic'],
          projectedLift: '+12%',
          confidence: '87%',
          duration: '6 weeks',
          insights: 'Peak traffic periods in urban stations show high potential for NFR attachment when properly incentivized.'
        }
      ],
      highway: [
        {
          id: 'hwy-cam-1',
          name: 'Highway Long-Haul Program',
          description: 'Tiered discounts for high-volume fuel purchases targeting commercial transportation customers.',
          impact: 'high',
          kpis: ['Volume', 'Loyalty', 'Customer Retention'],
          projectedLift: '+15%',
          confidence: '89%',
          duration: '8 weeks',
          insights: 'Commercial customers on highway routes prioritize volume discounts and consistent pricing.'
        },
        {
          id: 'hwy-cam-2',
          name: 'Rest Stop Revive',
          description: 'Café Amazon + restroom facility promotion to increase dwell time and non-fuel purchases.',
          impact: 'medium',
          kpis: ['NFR Sales', 'Basket Size', 'Customer Satisfaction'],
          projectedLift: '+9%',
          confidence: '84%',
          duration: '4 weeks',
          insights: 'Highway travelers value clean facilities and quick refreshment options during journey breaks.'
        }
      ],
      rural: [
        {
          id: 'rur-cam-1',
          name: 'Rural Community Card',
          description: 'Community-based loyalty program with local resident benefits and agricultural business incentives.',
          impact: 'high',
          kpis: ['Loyalty', 'Customer Retention', 'Volume'],
          projectedLift: '+11%',
          confidence: '90%',
          duration: '12 weeks',
          insights: 'Rural customers value community connection and personalized service over pure price incentives.'
        },
        {
          id: 'rur-cam-2',
          name: 'Competitive Loyalty Offers',
          description: 'High point loyalty offers on fuel and convenience aligned with competitive activities.',
          impact: 'high',
          kpis: ['Volume', 'Margin', 'Customer Satisfaction'],
          projectedLift: '+18%',
          confidence: '93%',
          duration: '6 weeks',
          insights: 'Attractive loyalty program offering high loyalty points to the customers in ordrer to provide a competitive advantage of recently opened competition stations in the trade area'
        }
      ],
      suburban: [
        {
          id: 'sub-cam-1',
          name: 'Family Weekend Special',
          description: 'Weekend promotion with family-oriented benefits and convenience store specials.',
          impact: 'medium',
          kpis: ['Traffic', 'Basket Size', 'NFR Sales'],
          projectedLift: '+7%',
          confidence: '85%',
          duration: '8 weeks',
          insights: 'Suburban customers make family shopping trips on weekends, creating bundling opportunities.'
        },
        {
          id: 'sub-cam-2',
          name: 'Weekday Commuter Program',
          description: 'Weekday morning coffee + fuel discount to capture daily commuter traffic.',
          impact: 'high',
          kpis: ['Traffic', 'Café Sales', 'Customer Retention'],
          projectedLift: '+13%',
          confidence: '88%',
          duration: '4 weeks',
          insights: 'Suburban commuters establish strong routine-based purchase patterns when properly incentivized.'
        }
      ]
    };
    
    return campaigns[segmentId] || campaigns.urban;
  };

  const handleImplementCampaign = (campaign) => {
    if (!selectedStations || selectedStations.length === 0) {
      setSelectedCampaign(campaign);
      setShowStationSelection(true);
    } else {
      toast.success(`Campaign "${campaign.name}" implemented to ${selectedStations.length} selected stations`);
    }
  };

  const campaigns = getCampaignsForSegment();

  // Generate station data for station selection modal
  const allStations = [
    { id: 'URB001', name: 'Central Bangkok', region: 'bangkok', format: 'flagship', volume: 5200 },
    { id: 'URB002', name: 'Silom Junction', region: 'bangkok', format: 'standard', volume: 4800 },
    { id: 'URB003', name: 'Sukhumvit 63', region: 'bangkok', format: 'flagship', volume: 4600 },
    { id: 'URB004', name: 'Ratchada Station', region: 'bangkok', format: 'standard', volume: 4300 },
    { id: 'URB005', name: 'Pattaya Main', region: 'eastern', format: 'flagship', volume: 4700 },
    { id: 'HWY001', name: 'Highway 7 (North)', region: 'central', format: 'standard', volume: 5800 },
    { id: 'HWY002', name: 'Highway 9 (East)', region: 'eastern', format: 'standard', volume: 5500 },
    { id: 'RUR001', name: 'Chiang Mai North', region: 'northern', format: 'standard', volume: 3200 },
    { id: 'RUR002', name: 'Khon Kaen Central', region: 'northern', format: 'standard', volume: 2900 },
    { id: 'SUB001', name: 'Pathum Thani', region: 'central', format: 'standard', volume: 3600 },
    { id: 'SUB002', name: 'Nonthaburi Central', region: 'central', format: 'flagship', volume: 3800 },
  ];
  
  // Filter relevant stations based on segment
  const segmentPrefix = {
    'urban': 'URB',
    'highway': 'HWY',
    'rural': 'RUR',
    'suburban': 'SUB'
  }[segmentId] || '';
  
  const filteredStations = allStations.filter(station => station.id.startsWith(segmentPrefix));

  return (
    <>
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
          <Lightbulb size={20} className="mr-2 text-yellow-500" />
          AI-Generated Campaign Recommendations
        </h3>
        <p className="text-sm text-gray-600">
          Based on your segment analysis, customer behavior patterns, and performance metrics,
          the following data-driven campaigns are recommended for implementation.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {campaigns.map((campaign) => (
          <div key={campaign.id} className="border border-gray-200 rounded-lg hover:shadow-md transition-all duration-300">
            <div className="border-b border-gray-200 bg-gray-50 p-4 rounded-t-lg">
              <div className="flex justify-between items-start">
                <h4 className="text-lg font-medium text-gray-900">{campaign.name}</h4>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  campaign.impact === 'high' 
                    ? 'bg-green-100 text-green-800' 
                    : campaign.impact === 'medium'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {campaign.impact.charAt(0).toUpperCase() + campaign.impact.slice(1)} Impact
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">{campaign.description}</p>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Target KPIs</div>
                  <div className="flex flex-wrap gap-1">
                    {campaign.kpis.map((kpi, index) => (
                      <span 
                        key={index}
                        className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full"
                      >
                        {kpi}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500 mb-1">Projected Lift</div>
                  <div className="flex items-center">
                    <TrendingUp size={16} className="text-green-500 mr-1" />
                    <span className="text-green-600 font-medium">{campaign.projectedLift}</span>
                    <span className="text-xs text-gray-500 ml-2">({campaign.confidence} confidence)</span>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500 mb-1">Recommended Duration</div>
                  <div className="flex items-center">
                    <Calendar size={14} className="text-gray-500 mr-1" />
                    <span className="text-gray-800">{campaign.duration}</span>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500 mb-1">Applicability</div>
                  <div className="flex items-center">
                    <Tag size={14} className="text-gray-500 mr-1" />
                    <span className="text-gray-800">{selectedStations.length > 0 ? `${selectedStations.length} stations` : 'All segment stations'}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-md p-3 mb-4">
                <div className="flex items-start">
                  <Users size={16} className="text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-blue-800 mb-1">Customer Insight</div>
                    <div className="text-xs text-blue-700">{campaign.insights}</div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between">
                <button className="text-purple-600 text-sm font-medium hover:text-purple-800 flex items-center">
                  View Details
                  <ArrowRight size={14} className="ml-1" />
                </button>
                <button 
                  className="bg-purple-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-purple-700 transition-colors flex items-center"
                  onClick={() => handleImplementCampaign(campaign)}
                >
                  <Check size={14} className="mr-1" />
                  Implement Promotion
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 flex justify-end">
        <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-200 transition-colors">
          Create Custom Campaign
        </button>
      </div>
      
      {showStationSelection && (
        <StationSelection
          campaign={selectedCampaign}
          stationData={filteredStations}
          onClose={() => setShowStationSelection(false)}
        />
      )}
    </>
  );
};

export default AICampaignRecommendations;

